import { registerDestructor } from '@ember/destroyable';
import { type Registry as Services, service } from '@ember/service';
import { waitForPromise } from '@ember/test-waiters';
import Modifier, { type ArgsFor } from 'ember-modifier';
import ENV from 'frontend-payments/config/environment';

interface DropinElement {
  mount: (element: HTMLElement) => void;
  unmount: () => void;
}

interface AdyenDropinSignature {
  Args: {
    Named: {
      adyenPaymentMethods: unknown;
      onAdditionalDetails: (state: unknown, dropin: unknown) => void;
      onAdyenDropinMounted: (dropin: unknown) => void;
      onSubmit: (state: unknown, dropin: unknown) => void;
    };
    Positional: [];
  };
  Element: HTMLElement;
}

export default class AdyenDropin extends Modifier<AdyenDropinSignature> {
  @service declare errorTracking: Services['error-tracking'];

  adyenDropin?: DropinElement;

  constructor(
    owner: ConstructorParameters<typeof Modifier>[0],
    args: ArgsFor<AdyenDropinSignature>,
  ) {
    super(owner, args);

    registerDestructor(this, () => {
      this.adyenDropin?.unmount();
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  async modify(
    element: AdyenDropinSignature['Element'],
    _: AdyenDropinSignature['Args']['Positional'],
    {
      adyenPaymentMethods,
      onSubmit,
      onAdditionalDetails,
      onAdyenDropinMounted,
    }: AdyenDropinSignature['Args']['Named'],
  ): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { default: adyenCheckout } = await waitForPromise(
      // @ts-expect-error: Incorrect type
      import('@adyen/adyen-web'),
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const checkout = await waitForPromise(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      adyenCheckout({
        clientKey: ENV.adyen.publicKey,
        locale: 'en_US',
        environment: ENV.adyen.environment,
        paymentMethodsResponse: adyenPaymentMethods,
        storePaymentMethod: true,
        paymentMethodsConfiguration: {
          card: {
            hasHolderName: true,
            holderNameRequired: true,
            name: 'Credit card',
            amount: {
              value: 0,
            },
          },
          ach: {
            hasHolderName: true,
            holderNameRequired: true,
            name: 'ACH payment',
            amount: {
              value: 0,
            },
          },
          directdebit_GB: {
            hasHolderName: true,
            holderNameRequired: true,
            name: 'Bacs direct debit',
            amount: {
              value: 0,
            },
          },
          sepadirectdebit: {
            hasHolderName: true,
            holderNameRequired: true,
            name: 'SEPA direct debit',
            amount: {
              value: 0,
            },
          },
        },
        onError: (error: unknown) => {
          this.errorTracking.captureException(error);
        },
        onSubmit,
        onAdditionalDetails,
        showPayButton: false,
      }),
    );

    // Check if the element still exists.
    // Because the modifier is async, it could be the element has unmounted by the time Adyen is loaded.
    if (!element) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const adyenDropin = checkout.create('dropin');
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    adyenDropin.mount(element);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.adyenDropin = adyenDropin;
    onAdyenDropinMounted(this.adyenDropin);
  }
}
